import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";

export default function Home() {
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-in");
          }
        });
      },
      { threshold: 0.1 }
    );

    sectionsRef.current.forEach((section) => {
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const addToRefs = (el) => {
    if (el && !sectionsRef.current.includes(el)) {
      sectionsRef.current.push(el);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section 
        ref={addToRefs}
        className="relative h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-indigo-50 overflow-hidden opacity-0 transform translate-y-8"
      >
        <div className="absolute top-12 left-0 w-full flex justify-center z-20">
          <img
            src="/sksu-logo.png"
            alt="SKSU Logo"
            className="h-32 w-32 md:h-44 md:w-44 object-contain mx-auto drop-shadow-xl rounded-full bg-white/95 p-2 border-4 border-blue-200"
            style={{ background: 'white' }}
          />
        </div>
        <div className="relative z-10 text-center px-6 max-w-6xl mx-auto pt-44 md:pt-56">
          <h1 className="text-5xl md:text-7xl font-light text-gray-900 mb-8 tracking-tight">
            <span className="font-bold">SKSU Digital Research Hub</span>
            <span className="block text-blue-600 font-medium">&amp; Open Access Journal</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Discover, share, and collaborate on groundbreaking research from Sultan Kudarat State University
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-blue-600 text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Get Started
            </Link>
            <Link
              to="/research"
              className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-full text-lg font-medium hover:border-blue-600 hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
            >
              Browse Research
            </Link>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-100 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-indigo-100 rounded-full opacity-30 animate-pulse delay-1000"></div>
      </section>

      {/* Features Section */}
      <section 
        ref={addToRefs}
        className="py-24 bg-white opacity-0 transform translate-y-8"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6">
              Why Choose Our
              <span className="block text-blue-600 font-medium">Repository?</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive platform designed for academic excellence and research collaboration
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center group">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-200 transition-colors duration-300">
                <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Comprehensive Database</h3>
              <p className="text-gray-600 leading-relaxed">
                Access thousands of research papers, theses, and academic publications from various disciplines
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-green-200 transition-colors duration-300">
                <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Collaborative Platform</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with researchers, share insights, and build academic networks across disciplines
              </p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-purple-200 transition-colors duration-300">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Advanced Analytics</h3>
              <p className="text-gray-600 leading-relaxed">
                Track research trends, analyze citations, and gain insights into academic impact
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section 
        ref={addToRefs}
        className="py-24 bg-gradient-to-r from-blue-600 to-indigo-700 text-white opacity-0 transform translate-y-8"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-light mb-6">
              Research
              <span className="block font-medium">Impact</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl md:text-5xl font-light mb-2">500+</div>
              <div className="text-blue-100 text-lg">Research Papers</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-light mb-2">50+</div>
              <div className="text-blue-100 text-lg">Academic Departments</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-light mb-2">1,000+</div>
              <div className="text-blue-100 text-lg">Active Researchers</div>
            </div>
            <div>
              <div className="text-4xl md:text-5xl font-light mb-2">10,000+</div>
              <div className="text-blue-100 text-lg">Downloads</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section 
        ref={addToRefs}
        className="py-24 bg-gray-50 opacity-0 transform translate-y-8"
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-6">
              How It
              <span className="block text-blue-600 font-medium">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-12">
            <div className="relative">
              <div className="text-6xl font-light text-blue-200 mb-4">01</div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Create Account</h3>
              <p className="text-gray-600 leading-relaxed">
                Sign up with your university credentials and get instant access to the repository
              </p>
            </div>

            <div className="relative">
              <div className="text-6xl font-light text-blue-200 mb-4">02</div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Upload Research</h3>
              <p className="text-gray-600 leading-relaxed">
                Share your research papers, theses, and academic publications with the community
              </p>
            </div>

            <div className="relative">
              <div className="text-6xl font-light text-blue-200 mb-4">03</div>
              <h3 className="text-2xl font-medium text-gray-900 mb-4">Collaborate & Grow</h3>
              <p className="text-gray-600 leading-relaxed">
                Connect with fellow researchers, receive feedback, and expand your academic network
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        ref={addToRefs}
        className="py-24 bg-white opacity-0 transform translate-y-8"
      >
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-6xl font-light text-gray-900 mb-8">
            Ready to Start Your
            <span className="block text-blue-600 font-medium">Research Journey?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Join thousands of researchers who are already making an impact through our platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/login"
              className="bg-blue-600 text-white px-12 py-5 rounded-full text-xl font-medium hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Get Started Today
            </Link>
            <Link
              to="/research"
              className="border-2 border-gray-300 text-gray-700 px-12 py-5 rounded-full text-xl font-medium hover:border-blue-600 hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
            >
              Explore Research
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-2xl font-medium mb-4">
                <span className="font-bold">SKSU Digital Research Hub</span> &amp; Open Access Journal
              </h3>
              <p className="text-gray-400 leading-relaxed">
                Empowering academic excellence through collaborative research and knowledge sharing.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-medium mb-4">About Us</h4>
              <p className="text-gray-400 leading-relaxed text-sm">
                The SKSU Research Repository was conceptualized, designed, and developed by <strong className="text-white">Engr. Alex Remegio</strong> as part of the university's digital transformation initiative to support student and faculty research visibility across all campuses of Sultan Kudarat State University.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-medium mb-4">Quick Links</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/research" className="hover:text-white transition-colors">Browse Research</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-medium mb-4">Support</h4>
              <ul className="space-y-2 text-gray-400">
                <li><button className="hover:text-white transition-colors text-left">Help Center</button></li>
                <li className="text-left">
                  <span className="block font-semibold text-white">Contact Us:</span>
                  <span className="block">ALEX REMEGIO, JR.</span>
                  <span className="block">Associate Professor/Web Developer</span>
                  <span className="block">Email: <a href="mailto:alexremegio@sksu.edu.ph" className="underline hover:text-blue-300">alexremegio@sksu.edu.ph</a></span>
                  <span className="block">Mobile: 09198787060 - Smart</span>
                </li>
                <li><button className="hover:text-white transition-colors text-left">FAQ</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Sultan Kudarat State University. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
} 